'use client';

import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '@/utils/client';
import axios from 'axios';
import { config } from '@/config';

import type { Event, EventPriority } from './types';

// Add CalendarEventType interface
interface CalendarEventType {
  id: string;
  name: string;
  color: string;
}

function noop(): void {
  return undefined;
}

interface EventCreateParams {
  title: string;
  description?: string;
  start: Date;
  end: Date;
  allDay: boolean;
  priority?: EventPriority;
  type?: string;
}

interface EventUpdateParams {
  title?: string;
  description?: string;
  start?: Date;
  end?: Date;
  allDay?: boolean;
  priority?: EventPriority;
  type?: string;
}

export interface CalendarContextValue {
  events: Map<string, Event>;
  eventTypes: CalendarEventType[];
  currentEventId?: string;
  setCurrentEventId: (eventId?: string) => void;
  createEvent: (params: EventCreateParams) => void;
  deleteEvent: (eventId: string) => void;
  updateEvent: (eventId: string, params: EventUpdateParams) => void;
  isLoading: boolean;
  isError: boolean;
  isLoadingEventTypes: boolean;
}

export const CalendarContext = React.createContext<CalendarContextValue>({
  events: new Map(),
  eventTypes: [],
  setCurrentEventId: noop,
  createEvent: noop,
  deleteEvent: noop,
  updateEvent: noop,
  isLoading: false,
  isError: false,
  isLoadingEventTypes: false,
});

export interface CalendarProviderProps {
  children: React.ReactNode;
}

// API functions
const fetchEvents = async (): Promise<Event[]> => {
  const response = await axiosClient.get('/api/v2/calendar/calendar-events');
  const data = response.data;
  return data.map((event: any) => ({
    id: event.id,
    title: event.title,
    description: event.description,
    start: new Date(event.start_date),
    end: new Date(event.end_date),
    allDay: event.all_day,
    priority: event.priority,
    type: event?.event_type?.name,
    color: event?.event_type?.color,
  }));
};

const fetchEventTypes = async (): Promise<CalendarEventType[]> => {
  const response = await axiosClient.get('/api/v2/calendar/calendar-events?getTypes=1');
  return response.data;
};

const createEventAPI = async (params: EventCreateParams): Promise<Event> => {
  await axios.get(`${config.site.api}/sanctum/csrf-cookie`);
  const response = await axios.post(`${config.site.api}/api/v2/calendar/calendar-events`, {
    title: params.title,
    description: params.description,
    start_date: params.start.toISOString(),
    end_date: params.end.toISOString(),
    all_day: params.allDay,
    ...(params.type?.trim() ? { type: params.type.trim() } : {}),
    ...(params as any).visibility ? { visibility: (params as any).visibility } : {},
  }, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      Accept: 'application/json',
    },
  });
  
  const data = response.data;
  return {
    id: data.id,
    title: data.title,
    description: data.description,
    start: new Date(data.start_date),
    end: new Date(data.end_date),
    allDay: data.all_day,
    priority: data.priority,
    type: data?.event_type?.name,
    color: data?.event_type?.color,
  };
};

const updateEventAPI = async (eventId: string, params: EventUpdateParams): Promise<Event> => {
  await axios.get(`${config.site.api}/sanctum/csrf-cookie`);
  const response = await axios.patch(`${config.site.api}/api/v2/calendar/calendar-events/${eventId}`, {
    title: params.title,
    description: params.description,
    start_date: params.start?.toISOString(),
    end_date: params.end?.toISOString(),
    all_day: params.allDay,
    ...(params.type?.trim() ? { type: params.type.trim() } : {}),
    ...(params as any).visibility ? { visibility: (params as any).visibility } : {},
  }, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      Accept: 'application/json',
    },
  });
  
  const data = response.data;
  return {
    id: data.id,
    title: data.title,
    description: data.description,
    start: new Date(data.start_date),
    end: new Date(data.end_date),
    allDay: data.all_day,
    priority: data.priority,
    type: data?.event_type?.name,
    color: data?.event_type?.color,
  };
};

const deleteEventAPI = async (eventId: string): Promise<void> => {
  await axios.get(`${config.site.api}/sanctum/csrf-cookie`);
  await axios.delete(`${config.site.api}/api/v2/calendar/calendar-events/${eventId}`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      Accept: 'application/json',
    },
  });
};

export function CalendarProvider({ children }: CalendarProviderProps): React.JSX.Element {
  const [currentEventId, setCurrentEventId] = React.useState<string>();
  const queryClient = useQueryClient();

  // Fetch events
  const { data: events = [], isLoading, isError } = useQuery({
    queryKey: ['calendar-events'],
    queryFn: fetchEvents,
  });

  // Fetch event types
  const { data: eventTypes = [], isLoading: isLoadingEventTypes } = useQuery({
    queryKey: ['calendar-event-types'],
    queryFn: fetchEventTypes,
  });

  // Create event mutation
  const createEventMutation = useMutation({
    mutationFn: createEventAPI,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
    },
  });

  // Update event mutation
  const updateEventMutation = useMutation({
    mutationFn: ({ eventId, params }: { eventId: string; params: EventUpdateParams }) =>
      updateEventAPI(eventId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
    },
  });

  // Delete event mutation
  const deleteEventMutation = useMutation({
    mutationFn: deleteEventAPI,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
    },
  });

  const handleCreateEvent = React.useCallback(
    (params: EventCreateParams) => {
      createEventMutation.mutate(params);
    },
    [createEventMutation]
  );

  const handleUpdateEvent = React.useCallback(
    (eventId: string, params: EventUpdateParams) => {
      updateEventMutation.mutate({ eventId, params });
    },
    [updateEventMutation]
  );

  const handleDeleteEvent = React.useCallback(
    (eventId: string) => {
      deleteEventMutation.mutate(eventId);
    },
    [deleteEventMutation]
  );

  // Convert events array to Map
  const eventsMap = React.useMemo(() => {
    return new Map(events.map((event) => [event.id, event]));
  }, [events]);

  return (
    <CalendarContext.Provider
      value={{
        events: eventsMap,
        eventTypes,
        currentEventId,
        setCurrentEventId,
        createEvent: handleCreateEvent,
        deleteEvent: handleDeleteEvent,
        updateEvent: handleUpdateEvent,
        isLoading,
        isError,
        isLoadingEventTypes,
      }}
    >
      {children}
    </CalendarContext.Provider>
  );
}
